import { useState, FC, useEffect } from 'react'
import { Alert, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import Dragger from 'antd/es/upload/Dragger';

import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ContainedButton, OutlinedButton } from '../styled';
import { aiCodeReview } from '../../../services/document.service';
import { incrementDocumentationStep, decrementDocumentationStep } from '../../../store/documentSlice';
import { useDispatch } from 'react-redux';
import UploadZipFileNote from './UploadZipFileNote';

interface CreateDocumentationProps {
  setBugsAndErrorHandlingReport: React.Dispatch<React.SetStateAction<any>>
  resetUpload: () => void
  requiredIssueDetails: any
  setSavedFile: React.Dispatch<React.SetStateAction<any>>
}

const CodeUploader: FC<CreateDocumentationProps> = ({ setBugsAndErrorHandlingReport, resetUpload, requiredIssueDetails, setSavedFile }) => {
  const [fileList, setFileList] = useState<any[]>([])
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<"warning" | "success" | "error">("error");
  const [loading, setLoading] = useState<boolean>(false)
  const dispatch = useDispatch()


  const setInfo = (message: string, severity: "warning" | "success" | "error") => {
    setMessage(message);
    setSeverity(severity);
  };

  const handleUpload = async () => {
    if (fileList?.length) {
      setLoading(true)
      try {
        const formData = new FormData();
        formData.append('file', fileList[0])
        formData.append('issueDetails', JSON.stringify(requiredIssueDetails))
        const documentRes = await aiCodeReview(formData)
        if(documentRes?.statusCode === 500) {
          setInfo('Something went wrong, please try again', "error")
          return;
        }
        if (documentRes) {
          setSavedFile(formData)
          setBugsAndErrorHandlingReport(documentRes)
          dispatch(incrementDocumentationStep())
        } else {
          setInfo('Something went wrong, please try again', "error")
        }
      } catch (e: any) {
        setInfo(e.message || 'Something went wrong, please try again', "error")
        console.error("Error uploading")
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    resetUpload()
    return () => {
      setLoading(false)
    }
  }, [])

  const onChange = ({ file }: any) => {
    setLoading(false)
    setInfo("", "error");
    const hasUploadedFile = file.length >= 1;

    const notValidFileTypes = ['image', 'video', 'audio']
    const fileType = file.type?.split('/')?.[0];

    if (hasUploadedFile) {
      setFileList([]);
      setInfo("You can only upload one file!", "error");
    } else if (notValidFileTypes?.includes(fileType)) {
      const fileName = file?.name?.toLowerCase();
      const allowedExtensions = ['.ts'];
      const isAllowedByExtension = allowedExtensions.some(ext => fileName?.endsWith(ext));
      if(isAllowedByExtension) {
        setFileList([file]);
        return;
      }
      setFileList([]);
      setInfo("You can only upload code files!", "error");
    } else {
      setFileList([file]);
    }
  };

  return (
    <div>
      <DialogTitle>
        <div className='document-header'>Upload Code</div>
        <div className='document-description'>Upload your code to create documentation. You may upload a single code file (e.g., .js, .ts, .py), or if your code depends on multiple files or components, please upload the entire project as a compressed ZIP file</div>
      </DialogTitle>
      <DialogContent className='fileUpload-body text-editor'>
        <Dragger
          beforeUpload={() => false}
          fileList={fileList}
          onChange={onChange}
          onRemove={() => setFileList([])}
          showUploadList={false}
          style={{ backgroundColor: "#FFFF" }}
        >
          <div className='dragger-inner-container'>
            <CloudUploadOutlinedIcon className='upload-icon' />
            <div className='uploader-text'>Upload a file or drag and drop</div>
            <div className='accepted-text'>PY, JS, TS up to 5MB</div>
          </div>
        </Dragger>
        {Boolean(fileList.length) && (
          <Alert
            color="info"
            severity="success"
            action={
              <IconButton size="small" onClick={() => setFileList([])}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            }
            style={{ marginTop: "20px" }}
          >
            {fileList[0].name}
          </Alert>
        )}
        {message && (
          <Alert
            severity={severity}
            action={
              <IconButton
                size="small"
                onClick={() => {
                  setInfo("", "error");
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            style={{ marginTop: "20px" }}
          >
            {message}
          </Alert>
        )}
        <UploadZipFileNote />
      </DialogContent>
      <DialogActions>
        <div className='dialog-action-container'>
          <OutlinedButton onClick={() => dispatch(decrementDocumentationStep())}>
            Back
          </OutlinedButton>
          <ContainedButton disabled={!fileList.length || loading} onClick={handleUpload}>
            {
              loading ? <CircularProgress style={{ color: 'white', marginLeft: '5px' }} size='1rem' />
                : 'Upload'
            }
          </ContainedButton>
        </div>
      </DialogActions>
    </div>
  )
}

export default CodeUploader